import {
    Component,
    OnInit,
    Input,
    SimpleChange,
    OnChanges,
    AfterViewChecked,
    HostListener,
} from '@angular/core';
import { Router } from '@angular/router';
import { NoDataTypes, NoDataSourceType, NoDataActionType } from '../../interfaces/no-data';
import { IRowItems, ITableTdItems } from '../../interfaces/card-data';
import { UserAccessService } from '../../services/user-access.service';
import { NavigationHelpService } from 'app/modules/shared/services/navigation-help.service';
import { ICardControllerInfo } from 'app/modules/shared/components/card-controller-panel/card-controller-panel.component';

export interface ICardListTableTitles {
    noTitle: string;
    sourceKey: string;
    type: string;
    typeKey: string;
    avgCredit: string;
    avgCreditKey: string;
    leadToFundPullThru: string;
    leadToFundPullThruKey: string;
}

export interface ICardlistData {
    cardId: string;
    type?: string;
    cardTitle: string;
    dataTableData?: IRowItems[];
    rowTitles?: ICardListTableTitles;
    hasData?: boolean;
    routerPath?: string;
    baseUrl?: string;
    isDataReady?: boolean;
    isTitleClickable?: boolean;
    action?: NoDataActionType;
    searchTerm?: string;
    hideViewAll: boolean;
    innerTableHeight?: string;
    outerTableHeight?: string;
    queryParams?: any;
    showCardControllers?: boolean;
    showPercentages?: boolean;
    urlSegment?: string;
}

@Component({
    selector: 'card-list',
    templateUrl: './card-list.component.html',
    styleUrls: ['./card-list.component.scss'],
})
export class CardListComponent implements OnInit, OnChanges, AfterViewChecked {
    @Input() cardListData: ICardlistData;
    cardTitle: string;
    rowTitles: ICardListTableTitles;
    hasData: boolean;
    noDataType: NoDataTypes;
    isTableLoaded = false;
    cardListContainerStyle: object;
    cardId: string;
    innerTableStyles: object;
    cardControllerInfo: ICardControllerInfo;

    constructor(
        private router: Router,
        private userAccessService: UserAccessService,
        private navigationHelpService: NavigationHelpService
    ) {}

    ngOnInit() {
        if (this.cardListData.outerTableHeight) {
            this.cardListContainerStyle = {
                height: this.cardListData.outerTableHeight,
            };
        }
        // TODO: make the inner-table height dynamically based on the outer-table height
        this.innerTableStyles = {
            'max-height': '300px',
        };

        if (this.cardListData && this.cardListData.innerTableHeight) {
            this.innerTableStyles['max-height'] = this.cardListData.innerTableHeight;
        }
    }

    ngAfterViewChecked() {
        let headerTr = document.getElementById('cardlist-table-header-tr' + this.cardId);
        if (headerTr && !this.isTableLoaded) {
            if (
                this.cardListData.hasData &&
                this.cardListData.dataTableData &&
                this.cardListData.isDataReady
            ) {
                this.setDataTableHeaderTdWidth();
                this.isTableLoaded = true;
            }
        }
    }

    ngOnChanges(changes: { [property: string]: SimpleChange }) {
        let change: SimpleChange = changes['cardListData'];
        this.updateData(change.currentValue);
    }

    updateData(cardData: ICardlistData) {
        if (cardData) {
            this.cardId = this.cardListData.cardId;
            this.cardControllerInfo = {
                cardId: cardData.cardId,
                showCardControllers: cardData.showCardControllers ?? false,
                cardTitle: cardData.cardTitle,
            };

            let hasAccess = true;
            //TODO: Implement a better solution to determine the access permision
            if (!cardData.hideViewAll) {
                let url = this.cardListData.routerPath ? this.cardListData.routerPath : '';
                hasAccess = this.userAccessService.hasAccessToPage(url);
            }

            this.isTableLoaded = false;
            this.cardTitle = cardData.cardTitle;
            this.rowTitles = cardData.rowTitles;
            this.noDataType = {
                action: cardData.action,
                searchTerm: cardData.searchTerm,
                componentTitle: cardData.cardTitle.replace(/[0-9]/g, ''),
                inputSource: NoDataSourceType.largeDataCard,
            };

            if (!hasAccess) {
                this.noDataType.action = NoDataActionType.lock;
                this.cardListData.hasData = false;
                this.cardListData.isDataReady = true;
            }
        }
    }

    clickViewAll(event) {
        this.navigationHelpService.navigateTo([this.cardListData.routerPath], {}, true);
    }

    clickTableCardItem(tdItem: ITableTdItems) {
        if (tdItem) {
            if (tdItem.hasAccess) {
                this.navigationHelpService.navigateTo(
                    [tdItem.routerLink, tdItem.urlSegment],
                    tdItem.queryParams,
                    true
                );
            }
        }
    }

    setDataTableHeaderTdWidth() {
        let headerTr = document.getElementById('cardlist-table-header-tr' + this.cardId);
        if (headerTr) {
            let totalDataTrTd = 0;
            let dataAreaTdWidth = document.getElementById(
                'cardlist-data-area-inner-td' + this.cardId
            ).clientWidth;
            let cardlistTableBodyArea = document.getElementById(
                'cardlist-table-body-area' + this.cardId
            );
            let scrollBarWidth =
                cardlistTableBodyArea.offsetWidth - cardlistTableBodyArea.clientWidth;
            let tableDataTr = document.getElementsByClassName('cardlist-data-tr' + this.cardId)[0];

            cardlistTableBodyArea.style.width =
                (dataAreaTdWidth - scrollBarWidth - 3).toString() + 'px';
            headerTr.style.width =
                (tableDataTr.clientWidth - (scrollBarWidth < 12 ? 12 : 0)).toString() + 'px';

            for (let i = 0; i < tableDataTr.children.length; i++) {
                let dataTrTd = tableDataTr.children[i];
                let headerTrTd = headerTr.children[i];
                headerTrTd.setAttribute('width', dataTrTd.clientWidth.toString() + 'px');
                totalDataTrTd += dataTrTd.clientWidth;
            }

            totalDataTrTd = totalDataTrTd - (scrollBarWidth < 12 ? 12 : 0);
            cardlistTableBodyArea.style.width =
                (totalDataTrTd + scrollBarWidth - 2).toString() + 'px';
            // TODO: set y-scroll bar position fixed
        }
    }

    clickTitle() {
        if (this.cardListData.isTitleClickable) {
            this.navigationHelpService.navigateTo(
                [this.cardListData.routerPath, this.cardListData.urlSegment],
                this.cardListData.queryParams,
                true
            );
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.setDataTableHeaderTdWidth();
    }
}
